import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { NavBar } from "@/components/NavBar";
import { useWorkshops } from "@/data/workshops";
import { trackSignupCompleted } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVELS = ["base", "intermedio", "avanzato"];

export default function Register() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { workshops, byId: WORKSHOPS, loading: workshopsLoading } = useWorkshops();
  const WORKSHOPS_LIST = workshops;

  const initialWorkshop = searchParams.get("workshop") || "tecnica";
  const initialLevel = searchParams.get("level") || "base";

  const getDefaultSession = (type, level) => {
    const ws = WORKSHOPS[type];
    if (!ws || !ws.sessions || ws.sessions.length === 0) return null;
    return (
      ws.sessions.find((s) => s.level.toLowerCase() === level) ||
      ws.sessions[0] ||
      null
    );
  };

  const urlDate = searchParams.get("date") || "";
  const urlPrice = searchParams.get("price") || "";
  const defaultSession = !urlDate
    ? getDefaultSession(initialWorkshop, initialLevel)
    : null;
  const initialDate = urlDate || (defaultSession ? defaultSession.date : "");
  const initialPrice = urlPrice || (defaultSession ? defaultSession.price : "");

  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Anti-spam: capture form load time + honeypot
  const formLoadTsRef = useRef(Date.now() / 1000);
  const [honeypot, setHoneypot] = useState("");

  const [form, setForm] = useState({
    workshop_type: initialWorkshop,
    workshop_level: initialLevel,
    workshop_date: initialDate,
    workshop_price: initialPrice,
    first_name: "",
    last_name: "",
    age: "",
    phone: "",
    email: "",
    city: "",
    has_danced_before: "",
    dance_type: "",
    tried_heels: "",
    expectations: "",
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // When workshops load asynchronously, sync default date/price if not already set
  useEffect(() => {
    if (workshopsLoading) return;
    if (form.workshop_date) return;
    const session = getDefaultSession(form.workshop_type, form.workshop_level);
    if (session) {
      setForm((f) => ({
        ...f,
        workshop_date: session.date,
        workshop_price: session.price,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workshopsLoading]);

  const handleWorkshopChange = (type) => {
    const ws = WORKSHOPS[type];
    const session = ws.sessions[0];
    setForm((f) => ({
      ...f,
      workshop_type: type,
      workshop_level: session.level.toLowerCase(),
      workshop_date: session.date,
      workshop_price: session.price,
    }));
  };

  const handleLevelChange = (level) => {
    const ws = WORKSHOPS[form.workshop_type];
    const session =
      ws.sessions.find((s) => s.level.toLowerCase() === level) ||
      ws.sessions[0];
    setForm((f) => ({
      ...f,
      workshop_level: level,
      workshop_date: session.date,
      workshop_price: session.price,
    }));
  };

  const canNext1 =
    form.workshop_type && form.workshop_level && form.workshop_date;
  const canNext2 =
    form.first_name &&
    form.last_name &&
    form.age &&
    form.codice_fiscale &&
    form.codice_fiscale.trim().length >= 11 &&
    form.phone &&
    form.email &&
    form.city;
  const canSubmit =
    canNext2 &&
    form.has_danced_before &&
    form.tried_heels &&
    form.expectations.trim().length > 5;

  const submit = async () => {
    if (!canSubmit) {
      toast.error("Completa tutti i campi del questionario");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/signups`, {
        ...form,
        age: parseInt(form.age, 10),
        codice_fiscale: form.codice_fiscale.trim().toUpperCase(),
        website: honeypot,
        form_load_ts: formLoadTsRef.current,
      });
      trackSignupCompleted({
        workshop_type: form.workshop_type,
        workshop_level: form.workshop_level,
        workshop_price: form.workshop_price,
      });
      setSuccess(true);
      toast.success("Iscrizione inviata! Ti contatteremo presto.");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Errore durante l'iscrizione";
      toast.error(typeof msg === "string" ? msg : "Errore. Riprova.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen sun-bg text-[#2A1810]">
        <NavBar />
        <div className="max-w-2xl mx-auto px-6 pt-32 pb-24 text-center">
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", duration: 0.7 }}
            className="w-20 h-20 mx-auto rounded-full border-2 border-[#A0522D] flex items-center justify-center mb-8 bg-[#FBF6EA]"
          >
            <Check size={36} className="text-[#A0522D]" />
          </motion.div>
          <p className="overline text-[#4F8FA8] mb-4">Iscrizione ricevuta</p>
          <h1 className="font-serif-display text-5xl sm:text-6xl tracking-tight">
            Ci vediamo
            <br />
            <span className="font-serif-italic text-[#A0522D]">in pista</span>.
          </h1>
          <p className="mt-6 text-[#6E5B4A] leading-relaxed">
            Grazie {form.first_name}! Ti ho appena inviato un&apos;email di conferma a{" "}
            <strong className="text-[#2A1810]">{form.email}</strong> con tutti
            i dettagli.
          </p>

          {/* Location */}
          <div
            data-testid="success-location"
            className="mt-10 text-left bg-[#FBF6EA] border-l-4 border-[#7AB8D6] rounded-sm p-5"
          >
            <p className="overline text-[#4F8FA8] mb-2">Dove ci troviamo</p>
            <p className="font-serif-display text-2xl text-[#2A1810]">
              Circolo Gandini
            </p>
            <p className="text-[#6E5B4A] text-sm mt-1">
              Via Pietro Nenni 15, 15121 Alessandria (AL)
            </p>
            <a
              href="https://www.google.com/maps/search/?api=1&query=Circolo+Gandini+Via+Pietro+Nenni+Alessandria"
              target="_blank"
              rel="noreferrer"
              className="inline-block mt-3 text-xs text-[#4F8FA8] border-b border-[#4F8FA8] pb-px hover:text-[#A0522D] hover:border-[#A0522D] transition"
            >
              Apri in Google Maps →
            </a>
          </div>

          {/* Payment */}
          <div
            data-testid="success-payment"
            className="mt-5 text-left bg-[#FBF6EA] border border-[#D9C5A4] rounded-sm p-5"
          >
            <p className="overline text-[#A0522D] mb-2">
              Per confermare il posto
            </p>
            <p className="text-[#2A1810] text-sm leading-relaxed">
              Versa un acconto di{" "}
              <strong className="text-[#A0522D]">€10</strong> su Revolut.
              <br />
              Il saldo rimanente lo porterai in <strong>contanti</strong> il
              giorno del workshop.
            </p>
            <a
              href="https://revolut.me/amantello04?currency=EUR&amount=1000"
              target="_blank"
              rel="noreferrer"
              data-testid="success-revolut-link"
              className="btn-primary mt-4 inline-flex"
            >
              Paga €10 su Revolut →
            </a>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="https://wa.me/393450416676"
              target="_blank"
              rel="noreferrer"
              data-testid="success-whatsapp"
              className="btn-ghost"
            >
              Scrivimi su WhatsApp
            </a>
            <button
              data-testid="success-home"
              onClick={() => navigate("/")}
              className="btn-ghost"
            >
              <ArrowLeft size={16} /> Torna alla home
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentWs = WORKSHOPS[form.workshop_type];

  return (
    <div className="min-h-screen sun-bg text-[#2A1810]">
      <NavBar />
      <div className="max-w-3xl mx-auto px-6 pt-32 pb-20">
        <div className="mb-10">
          <p className="overline text-[#4F8FA8]">Iscrizione · Step {step} di 3</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl mt-3">
            {step === 1 && "Scegli il tuo workshop"}
            {step === 2 && "I tuoi dati"}
            {step === 3 && "Raccontaci di te"}
          </h1>

          <div className="mt-6 flex gap-2" data-testid="step-progress">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className={`h-1 flex-1 rounded-full transition-colors ${
                  s <= step ? "bg-[#A0522D]" : "bg-[#D9C5A4]"
                }`}
              />
            ))}
          </div>
        </div>

        <form
          data-testid="registration-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (step === 3) submit();
          }}
          className="space-y-7"
        >
          {/* Honeypot field — hidden from humans, bots will fill it */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              left: "-9999px",
              width: "1px",
              height: "1px",
              overflow: "hidden",
              opacity: 0,
              pointerEvents: "none",
            }}
          >
            <label htmlFor="website">Sito web (lascia vuoto)</label>
            <input
              id="website"
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
            />
          </div>
          {step === 1 && (
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-7"
            >
              <div>
                <label className="overline text-[#6E5B4A] block mb-3">
                  Formato workshop
                </label>
                <div className="grid sm:grid-cols-3 gap-3">
                  {WORKSHOPS_LIST.map((ws) => (
                    <button
                      key={ws.id}
                      type="button"
                      data-testid={`select-workshop-${ws.id}`}
                      onClick={() => handleWorkshopChange(ws.id)}
                      className={`text-left p-4 rounded-sm border transition ${
                        form.workshop_type === ws.id
                          ? "border-[#A0522D] bg-[#A0522D]/10"
                          : "border-[#D9C5A4] bg-[#FBF6EA] hover:border-[#A0522D]/60"
                      }`}
                    >
                      <p className="font-serif-display text-xl text-[#2A1810]">
                        {ws.title}
                      </p>
                      <p className="text-xs text-[#6E5B4A] mt-1">{ws.tagline}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="overline text-[#6E5B4A] block mb-3">Livello</label>
                <div className="grid grid-cols-3 gap-3">
                  {LEVELS.map((lv) => (
                    <button
                      key={lv}
                      type="button"
                      data-testid={`select-level-${lv}`}
                      onClick={() => handleLevelChange(lv)}
                      className={`p-3 rounded-sm border transition capitalize ${
                        form.workshop_level === lv
                          ? "border-[#A0522D] bg-[#A0522D]/10 text-[#2A1810]"
                          : "border-[#D9C5A4] bg-[#FBF6EA] text-[#6E5B4A] hover:border-[#A0522D]/60"
                      }`}
                    >
                      {lv}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="overline text-[#6E5B4A] block mb-3">
                  Data disponibile
                </label>
                <Select
                  value={
                    form.workshop_date
                      ? `${form.workshop_date}|${form.workshop_price}`
                      : ""
                  }
                  onValueChange={(val) => {
                    if (!val) return;
                    const [date, price] = val.split("|");
                    update("workshop_date", date);
                    update("workshop_price", price);
                  }}
                >
                  <SelectTrigger
                    data-testid="select-date"
                    className="w-full h-auto py-3.5 px-4 bg-[#FBF6EA] border-[#D9C5A4] hover:border-[#A0522D]/60 focus:border-[#A0522D] focus:ring-0 text-[#2A1810] text-base rounded-md transition data-[placeholder]:text-[#A8987F]"
                  >
                    <SelectValue placeholder="Seleziona una data" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FBF6EA] border-[#D9C5A4] text-[#2A1810]">
                    {currentWs?.sessions
                      .filter(
                        (s) => s.level.toLowerCase() === form.workshop_level
                      )
                      .map((s) => (
                        <SelectItem
                          key={`${s.date}-${s.level}`}
                          value={`${s.date}|${s.price}`}
                          className="text-[#2A1810] focus:bg-[#A0522D]/15 focus:text-[#2A1810] data-[state=checked]:text-[#A0522D] cursor-pointer py-3"
                        >
                          {`${s.date} · ${s.time} · ${s.price}`}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              className="grid sm:grid-cols-2 gap-5"
            >
              <Field label="Nome">
                <input
                  data-testid="input-first-name"
                  type="text"
                  required
                  value={form.first_name}
                  onChange={(e) => update("first_name", e.target.value)}
                  className="input-warm"
                  placeholder="Maria"
                />
              </Field>
              <Field label="Cognome">
                <input
                  data-testid="input-last-name"
                  type="text"
                  required
                  value={form.last_name}
                  onChange={(e) => update("last_name", e.target.value)}
                  className="input-warm"
                  placeholder="Rossi"
                />
              </Field>
              <Field label="Età">
                <input
                  data-testid="input-age"
                  type="number"
                  min="10"
                  max="99"
                  required
                  value={form.age}
                  onChange={(e) => update("age", e.target.value)}
                  className="input-warm"
                  placeholder="25"
                />
              </Field>
              <Field label="Codice Fiscale">
                <input
                  data-testid="input-codice-fiscale"
                  type="text"
                  required
                  minLength={11}
                  maxLength={16}
                  value={form.codice_fiscale}
                  onChange={(e) =>
                    update("codice_fiscale", e.target.value.toUpperCase())
                  }
                  className="input-warm uppercase tracking-wider"
                  placeholder="RSSMRA85T10A562S"
                  autoComplete="off"
                />
              </Field>
              <Field label="Telefono">
                <input
                  data-testid="input-phone"
                  type="tel"
                  required
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  className="input-warm"
                  placeholder="+39 333 1234567"
                />
              </Field>
              <Field label="Email" className="sm:col-span-2">
                <input
                  data-testid="input-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  className="input-warm"
                  placeholder="maria.rossi@email.com"
                />
              </Field>
              <Field label="Città" className="sm:col-span-2">
                <input
                  data-testid="input-city"
                  type="text"
                  required
                  value={form.city}
                  onChange={(e) => update("city", e.target.value)}
                  className="input-warm"
                  placeholder="Milano"
                />
              </Field>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-7"
            >
              <Field label="Hai mai fatto danza?">
                <div className="flex gap-3">
                  {["si", "no"].map((v) => (
                    <button
                      key={v}
                      type="button"
                      data-testid={`btn-danced-${v}`}
                      onClick={() => update("has_danced_before", v)}
                      className={`flex-1 p-3 rounded-sm border transition capitalize ${
                        form.has_danced_before === v
                          ? "border-[#A0522D] bg-[#A0522D]/10 text-[#2A1810]"
                          : "border-[#D9C5A4] bg-[#FBF6EA] text-[#6E5B4A] hover:border-[#A0522D]/60"
                      }`}
                    >
                      {v === "si" ? "Sì" : "No"}
                    </button>
                  ))}
                </div>
              </Field>

              {form.has_danced_before === "si" && (
                <Field label="Che tipo di danza?">
                  <input
                    data-testid="input-dance-type"
                    type="text"
                    value={form.dance_type}
                    onChange={(e) => update("dance_type", e.target.value)}
                    className="input-warm"
                    placeholder="Hip Hop, Contemporanea, Modern Jazz…"
                  />
                </Field>
              )}

              <Field label="Hai mai provato heels dance?">
                <div className="flex gap-3">
                  {["si", "no"].map((v) => (
                    <button
                      key={v}
                      type="button"
                      data-testid={`btn-heels-${v}`}
                      onClick={() => update("tried_heels", v)}
                      className={`flex-1 p-3 rounded-sm border transition capitalize ${
                        form.tried_heels === v
                          ? "border-[#A0522D] bg-[#A0522D]/10 text-[#2A1810]"
                          : "border-[#D9C5A4] bg-[#FBF6EA] text-[#6E5B4A] hover:border-[#A0522D]/60"
                      }`}
                    >
                      {v === "si" ? "Sì" : "No"}
                    </button>
                  ))}
                </div>
              </Field>

              <Field label="Cosa ti aspetti da questa Summer Heels Experience?">
                <textarea
                  data-testid="input-expectations"
                  required
                  rows={4}
                  value={form.expectations}
                  onChange={(e) => update("expectations", e.target.value)}
                  className="input-warm resize-none"
                  placeholder="Racconta in poche righe le tue motivazioni e aspettative…"
                />
              </Field>
            </motion.div>
          )}

          <div className="flex justify-between pt-6 border-t border-[#D9C5A4]">
            {step > 1 ? (
              <button
                type="button"
                data-testid="btn-back"
                onClick={() => setStep(step - 1)}
                className="btn-ghost"
              >
                <ArrowLeft size={16} /> Indietro
              </button>
            ) : (
              <span />
            )}

            {step < 3 && (
              <button
                type="button"
                data-testid="btn-next"
                disabled={(step === 1 && !canNext1) || (step === 2 && !canNext2)}
                onClick={() => setStep(step + 1)}
                className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Avanti <ArrowRight size={16} />
              </button>
            )}

            {step === 3 && (
              <button
                type="submit"
                data-testid="btn-submit"
                disabled={!canSubmit || submitting}
                className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Invio…
                  </>
                ) : (
                  <>
                    Conferma iscrizione <Check size={16} />
                  </>
                )}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

const Field = ({ label, children, className = "" }) => (
  <div className={className}>
    <label className="overline text-[#6E5B4A] block mb-2">{label}</label>
    {children}
  </div>
);
